import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Phone, MapPin, Clock, Navigation, Plus, User, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { StatusBadge } from '../components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { useFleet } from '../contexts/FleetContext';
import { formatTmt, orderStatusLabels } from '../utils/format';
import { cn } from '../utils/cn';
export function Orders() {
  const {
    orders,
    drivers,
    assignDriver,
    addOrder
  } = useFleet();
  const pending = useMemo(() => orders.filter(o => o.status === 'new'), [orders]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(pending[0]?.id ?? null);
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState({
    customer: '',
    phone: '',
    pickup: '',
    dropoff: ''
  });
  const selectedOrder = orders.find(o => o.id === selectedOrderId) ?? null;
  const nearby = useMemo(() => drivers.filter(d => d.status === 'free').sort((a, b) => a.distanceKm - b.distanceKm), [drivers]);
  const selectedDriver = drivers.find(d => d.id === selectedDriverId) ?? null;
  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customer.trim() || !form.phone.trim() || !form.pickup.trim()) {
      toast.error('Заполните обязательные поля', {
        description: 'Имя клиента, телефон и адрес подачи.'
      });
      return;
    }
    const order = addOrder({
      customer: form.customer.trim(),
      phone: form.phone.trim(),
      pickup: form.pickup.trim(),
      dropoff: form.dropoff.trim() || undefined,
      price: 18 + Math.floor(Math.random() * 70)
    });
    setSelectedOrderId(order.id);
    setForm({
      customer: '',
      phone: '',
      pickup: '',
      dropoff: ''
    });
    toast.success(`Заказ ${order.id} создан`, {
      description: 'Заказ добавлен в очередь на назначение.'
    });
  };
  const handleAssign = () => {
    if (!selectedOrder || !selectedDriver) return;
    assignDriver(selectedOrder.id, selectedDriver.id);
    setConfirmOpen(false);
    toast.success(`Водитель назначен: ${selectedDriver.name}`, {
      description: `Уведомление отправлено. Адрес подачи: ${selectedOrder.pickup}. Телефон клиента: ${selectedOrder.phone}. Подача через ~${selectedDriver.etaMin} мин.`,
      duration: 6000
    });
    setSelectedDriverId(null);
    setSelectedOrderId(orders.find(o => o.status === 'new' && o.id !== selectedOrder.id)?.id ?? null);
  };
  return <div data-id="element-374" data-mp="4hnlsnlawwqh">
      <PageHeader title="Заказы" description={`В очереди: ${pending.length} · Всего за смену: ${orders.length}`} data-id="element-375" data-mp="9ee1h9t71qbd" />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3" data-id="element-376" data-mp="jh9n6eo9tqhu">
        {/* Новый заказ + очередь */}
        <div className="space-y-4" data-id="element-377" data-mp="ek9gq2nielq7">
          <Card data-id="element-378" data-mp="elb2p262csmp">
            <CardHeader className="pb-3" data-id="element-379" data-mp="9shxkd6onia3">
              <CardTitle className="text-base" data-id="element-380" data-mp="9rgdxkblpngr">Новый заказ</CardTitle>
            </CardHeader>
            <CardContent data-id="element-381" data-mp="5pv3s6qgbna9">
              <form className="space-y-3" onSubmit={handleCreate} data-id="element-382" data-mp="5qo0eevp4weo">
                <div className="space-y-1.5" data-id="element-383" data-mp="suw5rdyech1">
                  <Label htmlFor="customer" data-id="element-384" data-mp="pqnagpjmsk1">Имя клиента *</Label>
                  <Input id="customer" value={form.customer} onChange={e => setForm({
                  ...form,
                  customer: e.target.value
                })} placeholder="Огулджан Аширова" data-id="element-385" data-mp="3rxcw0nr4dl7r" />
                </div>
                <div className="space-y-1.5" data-id="element-386" data-mp="1l7y8fevn1g0r">
                  <Label htmlFor="phone" data-id="element-387" data-mp="1l5kfbbkiqdvu">Телефон *</Label>
                  <Input id="phone" value={form.phone} onChange={e => setForm({
                  ...form,
                  phone: e.target.value
                })} placeholder="+993 65 00-00-00" data-id="element-388" data-mp="1h74kwmm3y38e" />
                </div>
                <div className="space-y-1.5" data-id="element-389" data-mp="1f8v4m0vte5sd">
                  <Label htmlFor="pickup" data-id="element-390" data-mp="1f7tjf3l342wx">Адрес подачи *</Label>
                  <Input id="pickup" value={form.pickup} onChange={e => setForm({
                  ...form,
                  pickup: e.target.value
                })} placeholder="проспект Махтумкули, 22" data-id="element-391" data-mp="1j9ndgt0ry8o4" />
                </div>
                <div className="space-y-1.5" data-id="element-392" data-mp="vkm2ay0k4jf5">
                  <Label htmlFor="dropoff" data-id="element-393" data-mp="vlx44safzh3m">Адрес назначения</Label>
                  <Input id="dropoff" value={form.dropoff} onChange={e => setForm({
                  ...form,
                  dropoff: e.target.value
                })} placeholder="Необязательно" data-id="element-394" data-mp="10jfdgjsdfyc5" />
                </div>
                <Button type="submit" className="w-full" data-id="element-395" data-mp="rktpua81lt4i">
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-396" data-mp="7y08adx8id5s" />
                  Создать заказ
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card data-id="element-397" data-mp="gyye91r8gur0">
            <CardHeader className="pb-3" data-id="element-398" data-mp="gzgletffotdp">
              <CardTitle className="text-base" data-id="element-399" data-mp="cwbjot36gvq4">Очередь заказов</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[420px] space-y-2 overflow-y-auto p-3 pt-0" data-id="element-400" data-mp="qw32d74n30ag">
              {orders.map(order => <button key={order.id} type="button" onClick={() => setSelectedOrderId(order.id)} className={cn('w-full rounded-lg border p-3 text-left transition-colors', selectedOrderId === order.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent')} data-id="element-401" data-mp="lxrsiwecgps8">
                  <div className="flex items-center justify-between gap-2" data-id="element-402" data-mp="14osodbk6457k">
                    <span className="text-xs font-medium text-muted-foreground" data-id="element-403" data-mp="2auwjfdyboks">{order.id}</span>
                    <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-medium', order.status === 'new' && 'border-primary/30 bg-primary/10 text-primary', order.status === 'assigned' && 'border-amber-500/30 bg-amber-500/10 text-amber-700', order.status === 'done' && 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700')} data-id="element-404" data-mp="78d87djcshwh">
                      {orderStatusLabels[order.status]}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-foreground" data-id="element-405" data-mp="yz170ixk1b6z">{order.customer}</p>
                  <p className="truncate text-xs text-muted-foreground" data-id="element-406" data-mp="2wr5g7xkx9b4u">{order.pickup}</p>
                  <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground" data-id="element-407" data-mp="30qxjburjqp2t">
                    <Clock className="h-3 w-3" aria-hidden="true" data-id="element-408" data-mp="2t8g1905lu1y8" /> {order.createdAt} · {formatTmt(order.price)}
                  </p>
                </button>)}
            </CardContent>
          </Card>
        </div>

        {/* Детали заказа + подбор водителя */}
        <div className="space-y-4 xl:col-span-2" data-id="element-409" data-mp="2fh1lr3awoivx">
          <Card data-id="element-410" data-mp="2ficoczsm7klg">
            <CardHeader className="pb-3" data-id="element-411" data-mp="2feoufzj9cw67">
              <CardTitle className="text-base" data-id="element-412" data-mp="3c7k7601unflo">Детали заказа</CardTitle>
            </CardHeader>
            <CardContent data-id="element-413" data-mp="377fukl42b50q">
              {selectedOrder ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-414" data-mp="375v0sxpknq3x">
                  <div className="space-y-3" data-id="element-415" data-mp="334sujwx8mdsg">
                    <div className="flex items-center gap-2 text-sm" data-id="element-416" data-mp="2y7j9suksmqay">
                      <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-417" data-mp="2y9dldia6d62d" />
                      <span className="text-muted-foreground" data-id="element-418" data-mp="1rsiduovrq84d">Клиент:</span>
                      <span className="font-medium text-foreground" data-id="element-419" data-mp="1mrb4ildz90vl">{selectedOrder.customer}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm" data-id="element-420" data-mp="3mt29oo06pgv0">
                      <Phone className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-421" data-mp="3qx0bx7kt37sa" />
                      <span className="text-muted-foreground" data-id="element-422" data-mp="3cx7n5hsub1my">Телефон:</span>
                      <span className="font-medium text-foreground" data-id="element-423" data-mp="3huqzgc5z94c9">{selectedOrder.phone}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm" data-id="element-424" data-mp="9gicpkq4z7s0">
                      <MapPin className="mt-0.5 h-4 w-4 text-emerald-600" aria-hidden="true" data-id="element-425" data-mp="eeahvgfskqe5" />
                      <span data-id="element-426" data-mp="3vv1uzsi8f73r">
                        <span className="block text-muted-foreground" data-id="element-427" data-mp="3vu0fnbu6nyg8">Адрес подачи</span>
                        <span className="font-medium text-foreground" data-id="element-428" data-mp="4nz0ar36qios">{selectedOrder.pickup}</span>
                      </span>
                    </div>
                    <div className="flex items-start gap-2 text-sm" data-id="element-429" data-mp="sc8gndhmqe8j">
                      <Navigation className="mt-0.5 h-4 w-4 text-primary" aria-hidden="true" data-id="element-430" data-mp="x5tgzc6p3kaw" />
                      <span data-id="element-431" data-mp="2hu5p4dw89aze">
                        <span className="block text-muted-foreground" data-id="element-432" data-mp="2htce30q8jzbg">Адрес назначения</span>
                        <span className="font-medium text-foreground" data-id="element-433" data-mp="2cw3viz16dt9o">{selectedOrder.dropoff ?? 'Не указан'}</span>
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4" data-id="element-434" data-mp="2mmp1x8n3c235">
                    <div className="flex items-center justify-between text-sm" data-id="element-435" data-mp="1yx4h0k7dmur8">
                      <span className="text-muted-foreground" data-id="element-436" data-mp="1tyjfmhge2517">Номер заказа</span>
                      <span className="font-medium text-foreground" data-id="element-437" data-mp="27xsmgqipfmsi">{selectedOrder.id}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-438" data-mp="22x4t3y706tv4">
                      <span className="text-muted-foreground" data-id="element-439" data-mp="3jbwmadbz455h">Время создания</span>
                      <span className="font-medium text-foreground" data-id="element-440" data-mp="3edles995i6qf">{selectedOrder.createdAt}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-441" data-mp="2l5zh13lscjce">
                      <span className="text-muted-foreground" data-id="element-442" data-mp="2g64n0r6prdn4">Стоимость</span>
                      <span className="font-semibold text-foreground" data-id="element-443" data-mp="2g8za6qh43ck8">{formatTmt(selectedOrder.price)}</span>
                    </div>
                    {selectedOrder.comment && <p className="rounded-md bg-card p-2 text-xs text-muted-foreground" data-id="element-444" data-mp="27c8dqcyndvp4">
                        Комментарий: {selectedOrder.comment}
                      </p>}
                    {selectedOrder.status !== 'new' && <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-id="element-445" data-mp="1si3kbyw7urfm">
                        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" data-id="element-446" data-mp="1ogiayniw4va6" />
                        Водитель уже назначен
                      </p>}
                  </div>
                </div> : <p className="py-8 text-center text-sm text-muted-foreground" data-id="element-447" data-mp="3irdh7j6lry9s">
                  Выберите заказ из очереди, чтобы назначить водителя.
                </p>}
            </CardContent>
          </Card>

          <Card data-id="element-448" data-mp="1ebs72c3xfh3b">
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3" data-id="element-449" data-mp="1eb9z4i7wjefk">
              <CardTitle className="text-base" data-id="element-450" data-mp="1idn3b27034a8">Ближайшие свободные водители</CardTitle>
              <span className="text-xs text-muted-foreground" data-id="element-451" data-mp="ln4dlt90xleo">Найдено: {nearby.length}</span>
            </CardHeader>
            <CardContent className="space-y-2 p-3 pt-0" data-id="element-452" data-mp="ql5xgge9xor7">
              {nearby.map(driver => <div key={driver.id} className={cn('flex flex-wrap items-center gap-3 rounded-lg border p-3 transition-colors', selectedDriverId === driver.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent')} data-id="element-453" data-mp="un9m66pyepuf">
                  <button type="button" onClick={() => setSelectedDriverId(driver.id)} className="min-w-0 flex-1 text-left" data-id="element-454" data-mp="2azy6mklzrjaq">
                    <span className="flex items-center gap-2" data-id="element-455" data-mp="700w9eelgvsi">
                      <span className="text-sm font-medium text-foreground" data-id="element-456" data-mp="kwo5afjtnces">{driver.name}</span>
                      <StatusBadge status={driver.status} data-id="element-457" data-mp="fzo006usqdoq" />
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground" data-id="element-458" data-mp="3odo74yn4qqu3">
                      {driver.car} · {driver.plate} · {driver.location}
                    </span>
                  </button>
                  <div className="text-right" data-id="element-459" data-mp="1zwme786yzb9">
                    <p className="text-sm font-medium text-foreground" data-id="element-460" data-mp="20ocj2j0s4u5">{driver.distanceKm} км</p>
                    <p className="text-xs text-muted-foreground" data-id="element-461" data-mp="3tbpn3ap9ea1c">подача ~{driver.etaMin} мин</p>
                  </div>
                  <Button size="sm" disabled={!selectedOrder || selectedOrder.status !== 'new'} onClick={() => {
                setSelectedDriverId(driver.id);
                setConfirmOpen(true);
              }} data-id="element-462" data-mp="30p4wicgly1yu">
                    Назначить водителя
                  </Button>
                </div>)}
              {nearby.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground" data-id="element-463" data-mp="1b7zob06jxr4l">
                  Свободных водителей рядом нет. Попробуйте позже.
                </p>}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen} data-id="element-464" data-mp="1px5kefa3f189">
        <DialogContent data-id="element-465" data-mp="1pyqek3shwi5l">
          <DialogHeader data-id="element-466" data-mp="1uvgtgnskd7pf">
            <DialogTitle data-id="element-467" data-mp="1uunvocbl1sjf">Подтвердите назначение</DialogTitle>
            <DialogDescription data-id="element-468" data-mp="ehgvvjh6xu5c">
              Водитель получит push-уведомление с адресом подачи и телефоном клиента.
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && selectedDriver && <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4 text-sm" data-id="element-469" data-mp="yqqkkmvmbzkq">
              <div className="flex justify-between" data-id="element-470" data-mp="tyyojwtp11n7">
                <span className="text-muted-foreground" data-id="element-471" data-mp="txdus9f7dmqe">Заказ</span>
                <span className="font-medium text-foreground" data-id="element-472" data-mp="p1f94tl9eito">{selectedOrder.id}</span>
              </div>
              <div className="flex justify-between" data-id="element-473" data-mp="l03h6hewxlib">
                <span className="text-muted-foreground" data-id="element-474" data-mp="1hot1sfhxcs7z">Водитель</span>
                <span className="font-medium text-foreground" data-id="element-475" data-mp="1hmyq7rsjmcgk">{selectedDriver.name}</span>
              </div>
              <div className="flex justify-between gap-4" data-id="element-476" data-mp="1co5cpzd9alge">
                <span className="text-muted-foreground" data-id="element-477" data-mp="18n1vb8fk8hd2">Адрес подачи</span>
                <span className="text-right font-medium text-foreground" data-id="element-478" data-mp="13q34ayr70qj3">{selectedOrder.pickup}</span>
              </div>
              <div className="flex justify-between" data-id="element-479" data-mp="3tfdi3m72gkoi">
                <span className="text-muted-foreground" data-id="element-480" data-mp="3ofirljr7dx3z">Телефон клиента</span>
                <span className="font-medium text-foreground" data-id="element-481" data-mp="3ohlrcvi5r4lw">{selectedOrder.phone}</span>
              </div>
              <div className="flex justify-between" data-id="element-482" data-mp="23vndw1je8o4f">
                <span className="text-muted-foreground" data-id="element-483" data-mp="28xcqvmq6fbv5">Время подачи</span>
                <span className="font-medium text-foreground" data-id="element-484" data-mp="2cxmuwtuers7d">~{selectedDriver.etaMin} мин</span>
              </div>
            </div>}
          <DialogFooter data-id="element-485" data-mp="2hq67pol9ujb4">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-id="element-486" data-mp="2hqpov333l3o3">
              Отмена
            </Button>
            <Button onClick={handleAssign} data-id="element-487" data-mp="2rmsvom8w9qhd">Подтвердить и отправить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}